import React from "react";
import {
  Mail,
  MapPin,
  MessageCircle,
  Send,
  ShieldCheck
} from "lucide-react";
import ReCAPTCHA from "react-google-recaptcha";

// reactstrap components
import {
  Button,
  Col,
  Container,
  Form,
  FormGroup,
  Input,
  Label,
  Row
} from "reactstrap";

// core components
import MainNavbar from "components/Navbars/MainNavbar.js";
import LandingPageHeader from "components/Headers/LandingPageHeader.js";
import DefaultFooter from "components/Footers/DefaultFooter.js";
import SEO from "components/SEO/SEO.js";

const emailAddress = "adriana@endgamedogs.com";
const facebookUrl = "https://www.facebook.com/endgamesportdogs";
const recaptchaSiteKey =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY ||
  "6LcaUHctAAAAAJco7yFRdC9SPQw8Xwwbf-sdAtuY";
const contactApiUrl =
  process.env.REACT_APP_CONTACT_API_URL || "/api/contact.php";

const initialFormState = {
  name: "",
  email: "",
  message: "",
  website: ""
};

function ContactPage() {
  const recaptchaRef = React.useRef(null);
  const [formData, setFormData] = React.useState(initialFormState);
  const [captchaToken, setCaptchaToken] = React.useState(null);
  const [formStatus, setFormStatus] = React.useState(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  React.useEffect(() => {
    document.body.classList.add("landing-page");
    document.body.classList.add("sidebar-collapse");
    document.documentElement.classList.remove("nav-open");
    return function cleanup() {
      document.body.classList.remove("landing-page");
      document.body.classList.remove("sidebar-collapse");
    };
  }, []);

  const handleChange = event => {
    const { name, value } = event.target;

    setFormData(currentFormData => ({
      ...currentFormData,
      [name]: value
    }));
    setFormStatus(null);
  };

  const handleCaptchaChange = token => {
    setCaptchaToken(token);
    setFormStatus(null);
  };

  const handleSubmit = async event => {
    event.preventDefault();

    if (formData.website) {
      return;
    }

    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setFormStatus({
        type: "error",
        message: "Please add your name, email, and message before sending."
      });
      return;
    }

    if (!captchaToken) {
      setFormStatus({
        type: "error",
        message: "Please complete the captcha before sending."
      });
      return;
    }

    setIsSubmitting(true);
    setFormStatus(null);

    try {
      const response = await fetch(contactApiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          email: formData.email.trim(),
          message: formData.message.trim(),
          website: formData.website,
          recaptchaToken: captchaToken
        })
      });
      const result = await response.json();

      if (!response.ok || !result.success) {
        throw new Error(
          result.message || "Something went wrong sending your message."
        );
      }

      setFormStatus({
        type: "success",
        message: "Thanks! Your message has been sent."
      });
      setFormData(initialFormState);
    } catch (error) {
      setFormStatus({
        type: "error",
        message:
          error.message ||
          "Something went wrong sending your message. Please try again or email us directly."
      });
    } finally {
      setIsSubmitting(false);
      setCaptchaToken(null);

      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    }
  };

  return (
    <>
      <SEO
        title="Contact"
        description="Contact Endgame Sport Dogs near Raleigh, North Carolina with questions about planned litters, sport dog puppies, border collies, and sport mixes."
        path="/contact"
      />
      <MainNavbar />
      <div className="wrapper contact-page site-bg-light-grey">
        <LandingPageHeader
          bg="header5"
          hideText
          title="Contact Endgame Sport Dogs"
          subtitle="Reach out about planned litters, puppy inquiries, or questions about the program."
        />
        <main className="contact-main site-bg-light-grey">
          <section className="contact-section">
            <Container>
              <div className="contact-page-heading">
                <h2>Start the conversation</h2>
                <span aria-hidden="true"></span>
                <p>
                  Have a puppy inquiry, litter question, or want to learn more about the program? Send a note with a little context and we will get back to you.
                </p>
              </div>

              <Row className="contact-layout">
                <Col lg="5" className="mb-4 mb-lg-0">
                  <aside className="contact-info-panel">
                    <p className="contact-eyebrow">Best ways to reach us</p>
                    <h2>Facebook is usually fastest.</h2>
                    <p>
                      Emails occasionally land in spam, so Facebook is preferred for initial contact. Email is ok for those who do not have Facebook!
                    </p>

                    <div className="contact-methods">
                      <a href={facebookUrl} className="contact-method">
                        <MessageCircle aria-hidden="true" size={24} strokeWidth={2} />
                        <span>
                          <strong>Facebook</strong>
                          Endgame Sport Dogs
                        </span>
                      </a>

                      <a href={`mailto:${emailAddress}`} className="contact-method">
                        <Mail aria-hidden="true" size={24} strokeWidth={2} />
                        <span>
                          <strong>Email</strong>
                          {emailAddress}
                        </span>
                      </a>

                      <div className="contact-method">
                        <MapPin aria-hidden="true" size={24} strokeWidth={2} />
                        <span>
                          <strong>Location</strong>
                          Near Raleigh, North Carolina
                        </span>
                      </div>
                    </div>
                  </aside>
                </Col>

                <Col lg="7">
                  <ContactForm
                    formData={formData}
                    formStatus={formStatus}
                    onChange={handleChange}
                    onCaptchaChange={handleCaptchaChange}
                    onSubmit={handleSubmit}
                    recaptchaRef={recaptchaRef}
                    isSubmitting={isSubmitting}
                  />
                </Col>
              </Row>
            </Container>
          </section>
        </main>
        <DefaultFooter />
      </div>
    </>
  );
}

function ContactForm({
  formData,
  formStatus,
  onChange,
  onCaptchaChange,
  recaptchaRef,
  isSubmitting,
  onSubmit
}) {
  return (
    <Form className="contact-form-panel" onSubmit={onSubmit}>
      <div className="contact-form-heading">
        <h2>Send an inquiry</h2>
      </div>

      <Input
        aria-hidden="true"
        autoComplete="off"
        className="contact-honeypot"
        name="website"
        onChange={onChange}
        tabIndex={-1}
        type="text"
        value={formData.website}
      />

      <Row>
        <Col md="6">
          <FormGroup>
            <Label for="contact-name">Name</Label>
            <Input
              id="contact-name"
              name="name"
              onChange={onChange}
              placeholder="Your name"
              required
              type="text"
              value={formData.name}
            />
          </FormGroup>
        </Col>
        <Col md="6">
          <FormGroup>
            <Label for="contact-email">Email</Label>
            <Input
              id="contact-email"
              name="email"
              onChange={onChange}
              placeholder="you@example.com"
              required
              type="email"
              value={formData.email}
            />
          </FormGroup>
        </Col>
      </Row>

      <FormGroup>
        <Label for="contact-message">Message</Label>
        <Input
          id="contact-message"
          name="message"
          onChange={onChange}
          placeholder="Tell us about yourself, your goals, and what you would like to know."
          required
          rows="7"
          type="textarea"
          value={formData.message}
        />
      </FormGroup>

      <div className="contact-captcha">
        <div>
          <p className="contact-captcha-label">
            <ShieldCheck aria-hidden="true" size={18} strokeWidth={2.25} />
            Captcha
          </p>
        </div>
        <div className="contact-captcha-widget">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={recaptchaSiteKey}
            onChange={onCaptchaChange}
            onExpired={() => onCaptchaChange(null)}
          />
        </div>
      </div>

      {formStatus && (
        <p
          aria-live="polite"
          className={`contact-form-status contact-form-status-${formStatus.type}`}
        >
          {formStatus.message}
        </p>
      )}

      <Button className="contact-submit action-btn" disabled={isSubmitting} type="submit">
        <span>{isSubmitting ? "Sending..." : "Send Message"}</span>
        <Send aria-hidden="true" size={18} strokeWidth={2.25} />
      </Button>
    </Form>
  );
}

export default ContactPage;
